import { DT, FIREBALL, ORB, TELEPORT, applyTeleport, stepPlayer } from '@blorbs/common';
import type { FireballState, InputMessage, PlayerState, Snapshot, Vec } from '@blorbs/common';

const ZERO: Vec = { x: 0, y: 0 };
/** Corrections larger than this snap instead of easing (something big happened, e.g. a respawn). */
const SNAP_DISTANCE = 140;
/** A predicted fireball the server never confirmed disappears after this long. */
const GHOST_TTL_MS = 700;

/**
 * Client-side prediction for the local orb.
 *
 * Every tick the client samples input, steps its own orb through the shared
 * physics immediately, and sends the input with a sequence number. When a
 * snapshot arrives, the server's authoritative state (which includes hits,
 * knockback and collisions we can't predict) replaces ours and every input
 * the server hasn't applied yet is replayed on top. The difference between
 * where we were drawing the orb and where it now is gets eased out over a
 * few frames instead of snapping.
 */
export class Predictor {
  private seq = 0;
  private pending: InputMessage[] = [];
  private state: PlayerState | null = null;
  private errX = 0;
  private errY = 0;

  /**
   * One predicted tick. A teleport request is applied immediately (and
   * travels inside the input so the server applies it on the same tick and
   * replay reproduces it); it's dropped here if our own cooldown says no.
   * Returns the message, plus where we jumped from if we did.
   */
  step(move: Vec, aim: Vec, islandRadius: number, frozen: boolean, teleport: Vec | null, serverNow: number): { msg: InputMessage; jumpedFrom: Vec | null } {
    const msg: InputMessage = { seq: this.seq++, move: { ...move }, aim: { ...aim } };
    let jumpedFrom: Vec | null = null;
    const s = this.state;
    if (teleport && s?.alive && !frozen && serverNow >= s.teleportCooldownUntil) {
      msg.teleport = { ...teleport };
      jumpedFrom = applyTeleport(s, teleport);
      s.teleportCooldownUntil = serverNow + TELEPORT.cooldownMs;
      this.errX = this.errY = 0; // a jump is a jump; don't ease across it
    }
    this.pending.push(msg);
    if (this.pending.length > 90) this.pending.shift();
    if (s?.alive) {
      stepPlayer(s, frozen ? ZERO : move, islandRadius, DT, false);
      s.aim = aim;
    }
    return { msg, jumpedFrom };
  }

  reconcile(server: PlayerState, islandRadius: number, frozen: boolean): void {
    const prev = this.state;
    const next: PlayerState = { ...server, aim: { ...server.aim } };
    this.pending = this.pending.filter((i) => i.seq > server.lastInputSeq);
    if (next.alive) {
      for (const i of this.pending) {
        if (i.teleport) applyTeleport(next, i.teleport);
        stepPlayer(next, frozen ? ZERO : i.move, islandRadius, DT, false);
        next.aim = i.aim;
      }
    }
    if (prev?.alive && next.alive) {
      this.errX += prev.x - next.x;
      this.errY += prev.y - next.y;
      if (Math.hypot(this.errX, this.errY) > SNAP_DISTANCE) this.errX = this.errY = 0;
    } else {
      this.errX = this.errY = 0;
    }
    this.state = next;
  }

  update(dt: number): void {
    const k = Math.exp(-dt * 14);
    this.errX *= k;
    this.errY *= k;
  }

  /** The orb to draw: predicted state plus the easing correction. */
  view(): PlayerState | null {
    return this.state ? { ...this.state, x: this.state.x + this.errX, y: this.state.y + this.errY } : null;
  }

  position(): Vec | null {
    return this.state ? { x: this.state.x, y: this.state.y } : null;
  }
}

/**
 * Fireballs shown the instant you click. Each one is retired when the server's
 * real fireball with our name on it shows up, or after GHOST_TTL_MS if it never
 * does (cooldown rejected server-side, packet lost).
 */
export class GhostFireballs {
  private ghosts: Array<FireballState & { born: number }> = [];
  private seen = new Set<number>();
  private nextId = -1;
  /** residual ghost→real offset per server fireball, eased out after the handoff */
  private handoff = new Map<number, { dx: number; dy: number; at: number }>();
  /** local time (performance.now) when we can shoot again */
  cooldownUntil = 0;

  tryShoot(from: Vec, dir: Vec, now: number): boolean {
    if (now < this.cooldownUntil) return false;
    this.cooldownUntil = now + FIREBALL.cooldownMs;
    const spawn = ORB.radius + FIREBALL.radius + 2;
    this.ghosts.push({
      id: this.nextId--,
      x: from.x + dir.x * spawn,
      y: from.y + dir.y * spawn,
      dx: dir.x,
      dy: dir.y,
      owner: 'me',
      left: FIREBALL.range,
      born: now,
    });
    return true;
  }

  update(dt: number, now: number): void {
    for (const g of this.ghosts) {
      const d = FIREBALL.speed * dt;
      g.x += g.dx * d;
      g.y += g.dy * d;
      g.left -= d;
    }
    this.ghosts = this.ghosts.filter((g) => now - g.born < GHOST_TTL_MS && g.left > 0);
  }

  /**
   * Retire one ghost per newly-seen fireball of ours. Whatever gap is left
   * between where the ghost was drawn and where the real one is (jitter, the
   * server's lead cap) is remembered and eased out rather than snapped.
   */
  onSnapshot(snap: Snapshot, myId: string, realPos: (f: FireballState) => Vec, now: number, onHandoff?: (ghostId: number, realId: number) => void): void {
    for (const f of snap.fireballs) {
      if (f.owner !== myId || this.seen.has(f.id)) continue;
      this.seen.add(f.id);
      const ghost = this.ghosts.shift();
      if (ghost) {
        const p = realPos(f);
        this.handoff.set(f.id, { dx: ghost.x - p.x, dy: ghost.y - p.y, at: now });
        onHandoff?.(ghost.id, f.id);
      }
    }
    if (this.seen.size > 500) this.seen.clear();
    for (const [id, h] of this.handoff) if (now - h.at > 400) this.handoff.delete(id);
  }

  /** Offset to add to a real fireball's rendered position during the handoff (decays to zero). */
  offset(id: number, now: number): Vec | null {
    const h = this.handoff.get(id);
    if (!h) return null;
    const k = Math.exp(-(now - h.at) / 90);
    return { x: h.dx * k, y: h.dy * k };
  }

  list(): FireballState[] {
    return this.ghosts;
  }

  remove(id: number): void {
    this.ghosts = this.ghosts.filter((g) => g.id !== id);
  }
}

/**
 * Impact prediction. Server hits arrive RTT/2 after they happen; on screen we
 * can see a fireball touch an orb before that, so play the burst immediately
 * and dedupe when the server's event follows. Our own predicted fireballs
 * (negative ids) are matched to the server's event by attacker instead, since
 * their real id isn't known yet.
 */
export class HitPredictor {
  private predicted = new Map<number, number>(); // server fireball id → local time
  private ownPending: number[] = []; // local times of predicted hits from our own ghosts
  private hidden = new Set<number>();

  /** Returns fireballs that visually hit something this frame (already recorded). */
  check(fireballs: FireballState[], players: PlayerState[], myId: string, now: number): FireballState[] {
    const hits: FireballState[] = [];
    for (const f of fireballs) {
      if (this.hidden.has(f.id) || this.predicted.has(f.id)) continue;
      const owner = f.owner === 'me' ? myId : f.owner;
      for (const p of players) {
        if (!p.alive || p.id === owner) continue;
        if (Math.hypot(p.x - f.x, p.y - f.y) >= ORB.radius + FIREBALL.radius) continue;
        hits.push(f);
        if (f.id < 0) this.ownPending.push(now);
        else this.predicted.set(f.id, now);
        this.hidden.add(f.id);
        break;
      }
    }
    // Forget predictions the server never confirmed.
    for (const [id, t] of this.predicted) if (now - t > 1500) this.predicted.delete(id);
    this.ownPending = this.ownPending.filter((t) => now - t < 1500);
    return hits;
  }

  /** True if the burst for this server hit was already shown. */
  consume(fireballId: number, attackerId: string, myId: string): boolean {
    if (this.predicted.delete(fireballId)) return true;
    if (attackerId === myId && this.ownPending.length > 0) {
      this.ownPending.shift();
      return true;
    }
    return false;
  }

  /** Fireballs we've already exploded on screen; drop them from the draw list until the server removes them. */
  isHidden(id: number): boolean {
    return this.hidden.has(id);
  }

  prune(live: FireballState[]): void {
    if (this.hidden.size === 0) return;
    const ids = new Set(live.map((f) => f.id));
    for (const id of this.hidden) if (id > 0 && !ids.has(id)) this.hidden.delete(id);
  }
}
